import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiUpload, FiTrash2, FiFileText, FiLoader } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { regulatoryComplianceService, SubmitComplianceRequest } from '../../services/regulatoryComplianceService';

interface ComplianceSubmissionFormProps {
  organizationId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadedDocument {
  type: string;
  fileId: string;
  fileName: string;
  fileUrl: string;
}

export const ComplianceSubmissionForm: React.FC<ComplianceSubmissionFormProps> = ({
  organizationId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<Partial<SubmitComplianceRequest>>({
    phoneNumbers: [],
    country: 'AU',
    businessName: '',
    businessAddress: '',
    businessType: 'corporation',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    documents: [],
  });
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');

  const documentTypes = [
    'Business Registration Certificate',
    'ABN Registration',
    'Utility Bill',
    'Lease Agreement',
    'Bank Statement',
    'Drivers License',
    'Passport',
    'Letter of Authorization',
  ];

  const businessTypes = [
    { value: 'corporation', label: 'Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'sole_proprietor', label: 'Sole Proprietor' },
    { value: 'non_profit', label: 'Non-Profit' },
  ];

  const handleFileUpload = async (file: File, documentType: string) => {
    try {
      setUploading(true);
      const result = await regulatoryComplianceService.uploadFile(file, {
        type: documentType,
        organizationId,
      });

      const newDoc: UploadedDocument = {
        type: documentType,
        fileId: result.fileId,
        fileName: result.fileName,
        fileUrl: result.fileUrl,
      };

      setUploadedDocuments([...uploadedDocuments, newDoc]);
      toast.success(`${file.name} uploaded successfully`);
    } catch (error: any) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = (index: number) => {
    setUploadedDocuments(uploadedDocuments.filter((_, i) => i !== index));
  };

  const handleAddPhoneNumber = () => {
    if (phoneNumberInput.trim()) {
      const numbers = formData.phoneNumbers || [];
      if (!numbers.includes(phoneNumberInput.trim())) {
        setFormData({
          ...formData,
          phoneNumbers: [...numbers, phoneNumberInput.trim()],
        });
        setPhoneNumberInput('');
      }
    }
  };

  const handleRemovePhoneNumber = (number: string) => {
    setFormData({
      ...formData,
      phoneNumbers: (formData.phoneNumbers || []).filter((n) => n !== number),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.phoneNumbers?.length) {
      toast.error('Please add at least one phone number');
      return;
    }

    if (!formData.businessName || !formData.businessAddress) {
      toast.error('Please fill in all required business information');
      return;
    }

    if (!formData.contactName || !formData.contactEmail || !formData.contactPhone) {
      toast.error('Please fill in all required contact information');
      return;
    }

    if (uploadedDocuments.length === 0) {
      toast.error('Please upload at least one document');
      return;
    }

    try {
      setLoading(true);

      const submitData: SubmitComplianceRequest = {
        phoneNumbers: formData.phoneNumbers,
        country: formData.country!,
        businessName: formData.businessName!,
        businessAddress: formData.businessAddress!,
        businessType: formData.businessType!,
        contactName: formData.contactName!,
        contactEmail: formData.contactEmail!,
        contactPhone: formData.contactPhone!,
        documents: uploadedDocuments.map((doc) => ({
          type: doc.type,
          fileId: doc.fileId,
        })),
      };

      await regulatoryComplianceService.submitRequest(organizationId, submitData);
      onSuccess();
    } catch (error: any) {
      console.error('Submission failed:', error);
      toast.error(error.response?.data?.error || 'Failed to submit compliance request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-900 rounded-2xl border border-slate-700 max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white">Submit Compliance Request</h3>
            <p className="text-gray-400 mt-1">Australian Phone Number Regulatory Compliance</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Phone Numbers */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Phone Numbers <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={phoneNumberInput}
                onChange={(e) => setPhoneNumberInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPhoneNumber())}
                placeholder="+61 XXX XXX XXX"
                className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddPhoneNumber}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.phoneNumbers?.map((number) => (
                <span
                  key={number}
                  className="flex items-center gap-2 px-3 py-1 bg-slate-800 text-white rounded-lg"
                >
                  {number}
                  <button
                    type="button"
                    onClick={() => handleRemovePhoneNumber(number)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Business Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Business Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Business Type <span className="text-red-400">*</span>
              </label>
              <select
                required
                value={formData.businessType}
                onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              >
                {businessTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Business Address <span className="text-red-400">*</span>
            </label>
            <textarea
              required
              rows={3}
              value={formData.businessAddress}
              onChange={(e) => setFormData({ ...formData, businessAddress: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contact Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contact Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contact Phone <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Document Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Upload Documents <span className="text-red-400">*</span>
            </label>
            <div className="space-y-3">
              {documentTypes.map((docType) => (
                <div key={docType} className="flex items-center gap-3">
                  <label className="flex-1 flex items-center gap-3 p-3 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                    <FiUpload className="text-gray-400" />
                    <span className="flex-1 text-sm text-gray-300">{docType}</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, docType);
                      }}
                      accept=".pdf,.jpg,.jpeg,.png"
                      disabled={uploading}
                    />
                    {uploading && <FiLoader className="animate-spin text-blue-400" />}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Uploaded Documents */}
          {uploadedDocuments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Uploaded Documents
              </label>
              <div className="space-y-2">
                {uploadedDocuments.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FiFileText className="text-blue-400" />
                      <div>
                        <p className="text-sm text-white">{doc.fileName}</p>
                        <p className="text-xs text-gray-400">{doc.type}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <FiLoader className="animate-spin" />}
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
